import { action, computed, makeObservable, observable, runInAction } from 'mobx';

import { AxiosError } from 'axios';

import _ from 'lodash';

import DataMixin from 'utils/stores/DataMixin';

import { EntityModules, PAGE_SIZE } from 'core/constants';
import Logger from 'utils/logger';
import { readerDayKey } from 'utils/formatters/timezone';
import { timezoneStore } from 'stores/Timezone';
import API from 'utils/api';
import RootStore from 'stores/Root';
import EntitiesActivities from 'stores/EntitiesActivities';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import {
  EntityDetailsType,
  EntityHistoryResponseType,
  EntityValueType,
} from 'utils/api/modules/entities/entities.types';

export const CREATED_AT_KEY = 'createdAt';
export const IMPORTED_KEY = 'imported';
export const AMENDMENT_KEY = 'amendment';

export default abstract class EntityDetails<T extends EntityDetailsType = EntityDetailsType> extends DataMixin {
  @observable details?: T;

  @observable error?: AxiosError;

  @observable isContentEdited = false;
  @observable fieldErrors: Record<string, string> = {};

  entityType: EntityModules;

  path: string;

  detailsPath: (uuid: string) => string;

  @observable activities?: EntitiesActivities;

  prefix = '';

  @observable protected historyLoaded = false;

  @observable loadingHistory = false;

  @observable historyHasMore = false;

  @observable initialHistory?: EntityHistoryResponseType[];

  private historyFor?: string;

  private tailEntriesLoaded = false;

  private historyRowsFetched = 0;

  protected supportsHistoryImport = false;

  // A computed, so a change of the reader's zone regroups the days without a reload
  @computed get history(): Record<string, EntityHistoryResponseType[]> | undefined {
    return this.historyLoaded && this.initialHistory ? this.groupHistoryByCreatedAt(this.initialHistory) : undefined;
  }

  constructor(
    rootStore: RootStore,
    api: typeof API,
    entityType: EntityModules,
    path: string,
    detailsPath: (uuid: string) => string
  ) {
    super(rootStore, api);
    makeObservable(this);
    this.entityType = entityType;
    this.path = path;
    this.detailsPath = detailsPath;
    this.prefix = `${entityType}__`;
  }

  protected getCreationFormName(): string | null {
    return null;
  }

  protected wasImported(): boolean {
    return false;
  }

  protected async loadAmendmentHistoryEntries(_entityId: string): Promise<EntityHistoryResponseType[]> {
    return [];
  }

  @action.bound
  async loadHistory(entityId: string, clearHistory = false): Promise<void> {
    this.loadingHistory = true;
    const isFirstPage = clearHistory || this.historyFor !== entityId;
    if (isFirstPage) {
      this.initialHistory = [];
      this.historyLoaded = false;
      this.historyFor = entityId;
      this.tailEntriesLoaded = false;
      this.historyRowsFetched = 0;
    }

    const params = {
      limit: PAGE_SIZE,
      offset: this.historyRowsFetched,
    };

    try {
      const [
        {
          data: { results, next },
        },
        amendmentEntries,
      ] = await Promise.all([
        this.api.entities.loadEntityHistory(this.entityType, entityId, params)(),
        isFirstPage ? this.loadAmendmentHistoryEntries(entityId) : Promise.resolve([]),
      ]);

      this.historyRowsFetched += results.length;

      results.push(...amendmentEntries);

      if (!next && !this.tailEntriesLoaded) {
        this.tailEntriesLoaded = true;

        if (this.supportsHistoryImport && this.wasImported()) {
          results.push({
            createdAt: this.details?.createdAt || '',
            createdBy: null,
            current: null,
            previous: null,
            entityField: {
              key: IMPORTED_KEY,
              uuid: IMPORTED_KEY,
            },
          });
        }

        results.push({
          createdAt: this.details?.createdAt || '',
          createdBy: this.details?.createdBy || null,
          current: this.getCreationFormName(),
          previous: null,
          entityField: {
            key: CREATED_AT_KEY,
            uuid: CREATED_AT_KEY,
          },
        });
      }

      const allResults = [...(this.initialHistory || []), ...results];

      runInAction(() => {
        if (this.historyFor !== entityId) return;
        this.initialHistory = allResults;
        this.historyLoaded = true;
        this.historyHasMore = next !== null;
      });
    } catch (e) {
      Logger.error('Failed to load entity history', e);
    } finally {
      runInAction(() => {
        if (this.historyFor !== entityId) return;
        this.loadingHistory = false;
      });
    }
  }

  groupHistoryByCreatedAt(
    history: EntityHistoryResponseType[]
  ): Record<string, EntityHistoryResponseType[]> | undefined {
    const sortedHistory = _.orderBy(history, 'createdAt', 'desc');
    // Flag off keeps the UTC day History has always shown, not the primitives' browser day
    const grouped = _.groupBy(sortedHistory, (item) =>
      timezoneStore.enabled ? readerDayKey(item.createdAt) : item.createdAt.split('T')[0]
    );

    const orderedGrouped = _.fromPairs(_.orderBy(_.toPairs(grouped), ([day]) => day, 'desc'));

    return orderedGrouped;
  }

  getName(details: EntityDetailsType): string {
    return details.entity[`${this.prefix}name`]?.value as string;
  }

  // Abstract methods that all child stores must implement
  abstract loadDetails(entityId: string): Promise<void>;

  abstract create(entityName: string): Promise<boolean | string>;

  abstract delete(entityId: string): Promise<boolean>;

  abstract edit(data: Record<string, EntityValueType>): Promise<boolean | string[]>;

  abstract contentHasBeenEdited(questionId: string, newValue: AnswerTypes): void;

  private openEditForms = 0;

  // A section's edit form registers while mounted. Cancel, Save and switching tabs all unmount it, and the
  // record stops looking dirty once the last one closes (sections can be edited side by side).
  trackEditForm(): () => void {
    this.openEditForms += 1;
    return () => {
      this.openEditForms -= 1;
      if (this.openEditForms === 0) {
        runInAction(() => this.onEditFormsClosed());
      }
    };
  }

  protected onEditFormsClosed(): void {
    this.isContentEdited = false;
  }

  reset(): void {
    this.isFailure = false;
    this.isLoaded = false;
  }
}
